import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  CircleAlert,
  EllipsisVertical,
  Loader2,
  Play,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge, Button, DropdownMenu, IconButton } from 'slab'
import { capitalize } from 'utils'
import {
  getGetBrokersTaskWorkflowsIdQueryKey,
  getGetBrokersTaskWorkflowsQueryKey,
  usePatchBrokersTasks,
} from 'utils/web_api'
import { type BrokersTask } from 'utils/web_api.schemas'

import { BlockTaskDialog } from './BlockTaskDialog'
import { UnblockTaskDialog } from './UnblockTaskDialog'
import { sortTaskAvailableActions } from './WorkflowVisualIndicators'

const DangerousTransitionIconWithTooltip = () => {
  return (
    <DisplayTooltipOnHover
      text='The dependencies for this task are not resolved. Be sure this task is one you want to take action on as you could trigger execution of a task that is not ready for execution by changing dependencies.'
      tooltipClassName='w-80'
      asChild
    >
      <CircleAlert className='w-4 h-4 text-red-500' />
    </DisplayTooltipOnHover>
  )
}

// Task Settings Popover Component
const TaskSettingsPopover = ({
  task,
  workflowId,
  enrollmentId,
  withPortal,
}: {
  task: BrokersTask
  workflowId: string
  enrollmentId?: string
  withPortal?: boolean
}) => {
  const queryClient = useQueryClient()
  const taskMutation = usePatchBrokersTasks()

  const handleUpdateExecutionMethod = (method: 'automated' | 'manual') => {
    const message = `Task execution method changed from ${task.execution_method} to ${method} by broker`

    taskMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          method,
          method_message: message,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
          })
          // Invalidate workflow list if enrollment ID is available
          if (enrollmentId) {
            queryClient.invalidateQueries({
              queryKey: getGetBrokersTaskWorkflowsQueryKey({
                'filters[enrollment_ids][]': [enrollmentId],
                'sort[status]': 'asc',
              }),
            })
          }
        },
        onError: (error) => {
          console.error('Failed to update execution method:', error)
          alert('Failed to update execution method. Please try again.')
        },
      }
    )
  }

  const handleUpdateExecutionContext = (context: 'thatch' | 'member') => {
    const message = `Task execution context changed from ${task.execution_context} to ${context} by broker`

    taskMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          execution_context: context,
          context_message: message,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
          })
          // Invalidate workflow list if enrollment ID is available
          if (enrollmentId) {
            queryClient.invalidateQueries({
              queryKey: getGetBrokersTaskWorkflowsQueryKey({
                'filters[enrollment_ids][]': [enrollmentId],
                'sort[status]': 'asc',
              }),
            })
          }
        },
        onError: (error) => {
          console.error('Failed to update execution context:', error)
          alert('Failed to update execution context. Please try again.')
        },
      }
    )
  }

  return (
    <DropdownMenu
      align='end'
      withPortal={withPortal}
      trigger={
        <IconButton
          label='Task settings'
          variant='secondary'
          icon={EllipsisVertical}
          disabled={taskMutation.isPending}
        />
      }
    >
      {/* Execution Method Section */}
      <DropdownMenu.Section title='Method'>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionMethod('automated')}
          disabled={
            task.execution_method === 'automated' ||
            taskMutation.isPending ||
            !task.allowed_method_changes?.includes('automated')
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Automated</span>
            {task.execution_method === 'automated' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionMethod('manual')}
          disabled={
            task.execution_method === 'manual' ||
            taskMutation.isPending ||
            !task.allowed_method_changes?.includes('manual')
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Manual</span>
            {task.execution_method === 'manual' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Section>

      {/* Execution Context Section */}
      <DropdownMenu.Section title='Context'>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionContext('thatch')}
          disabled={
            task.execution_context === 'thatch' || taskMutation.isPending
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Thatch</span>
            {task.execution_context === 'thatch' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionContext('member')}
          disabled={
            task.execution_context === 'member' || taskMutation.isPending
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Member</span>
            {task.execution_context === 'member' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Section>
    </DropdownMenu>
  )
}

// Task Action Controls Component
const TaskActionControls = ({
  task,
  onTaskAction,
  isLoading,
  workflowId,
  enrollmentId,
  withPortal,
}: {
  task: BrokersTask
  onTaskAction: (taskId: string, action: string) => void
  isLoading: boolean
  workflowId: string
  enrollmentId?: string
  withPortal?: boolean
}) => {
  const [selectedAction, setSelectedAction] = useState<string>('')
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false)
  const [isUnblockDialogOpen, setIsUnblockDialogOpen] = useState(false)
  const { isUserThatchWorkflowAdmin } = useContext(BrokersMemberContext)

  // Filter out "complete" and "process" actions for automated tasks. Otherwise we should just display all actions for Ops to take
  // in case they want to move a task to manual before taking action on it.
  const filteredActions = useMemo(() => {
    const actions = task.available_actions || []
    if (task.execution_method === 'automated') {
      return actions.filter((action) => action !== 'complete' && action !== 'process')
    }
    return actions
  }, [task.available_actions, task.execution_method])

  // Memoize sorted actions to prevent unnecessary recalculation
  const sortedActions = useMemo(
    () => sortTaskAvailableActions(filteredActions),
    [filteredActions]
  )

  // Track previous available actions to detect real changes
  const prevAvailableActionsRef = useRef<string[]>([])

  // Auto-select first available action ONLY when available actions actually change
  useEffect(() => {
    const prevActions = prevAvailableActionsRef.current
    const currentActions = filteredActions

    // Check if the available actions actually changed (not just a re-render)
    const actionsChanged =
      prevActions.length !== currentActions.length ||
      prevActions.some((action, index) => action !== currentActions[index])

    if (actionsChanged) {
      // Actions changed - update the ref and reset to first action
      prevAvailableActionsRef.current = currentActions

      if (sortedActions && sortedActions.length > 0) {
        setSelectedAction(sortedActions[0])
      } else {
        setSelectedAction('')
      }
    }
  }, [filteredActions, sortedActions])

  const hasActions = filteredActions.length > 0

  const handleExecuteAction = () => {
    if (!selectedAction) return

    // Open dialogs for block/unblock actions
    if (selectedAction === 'block') {
      setIsBlockDialogOpen(true)
      return
    }

    if (selectedAction === 'unblock') {
      setIsUnblockDialogOpen(true)
      return
    }

    // For other actions, execute directly
    onTaskAction(task.id, selectedAction)
  }

  const getActionIcon = () => {
    if (isLoading) return <Loader2 className='w-3 h-3 animate-spin' />

    switch (selectedAction) {
      case 'process':
        return <Play className='w-4 h-4' />
      case 'complete':
        return <Check className='w-4 h-4' />
      case 'cancel':
        return <X className='w-4 h-4' />
      case 'uncancel':
        return <Play className='w-4 h-4' />
      case 'block':
        return <ShieldAlert className='w-4 h-4' />
      case 'unblock':
        return <ShieldCheck className='w-4 h-4' />
      case 'retry':
        return <RotateCcw className='w-4 h-4' />
      case 'rollback':
        return <RotateCcw className='w-4 h-4' />
      default:
        return <Check className='w-4 h-4' />
    }
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'complete':
        return 'Complete'
      case 'cancel':
        return 'Cancel'
      case 'uncancel':
        return 'Uncancel'
      case 'block':
        return 'Block'
      case 'unblock':
        return 'Return to Pending'
      case 'rollback':
        return 'Rollback'
      default:
        return capitalize(action)
    }
  }

  return (
    <>
      <div className='flex items-center space-x-1'>
        {(task.dependencies_resolved || isUserThatchWorkflowAdmin) && (
          <>
            {!task.dependencies_resolved && (
              <DangerousTransitionIconWithTooltip />
            )}
            <DropdownMenu
              withPortal={withPortal}
              trigger={
                <Button
                  variant='secondary'
                  size='small'
                  rightIcon={ChevronDown}
                  disabled={!hasActions}
                >
                  {hasActions && selectedAction
                    ? getActionLabel(selectedAction)
                    : 'No Actions Available'}
                </Button>
            }
            >
              {filteredActions.map((action) => (
                <DropdownMenu.Item
                  key={action}
                  onSelect={() => setSelectedAction(action)}
                >
                  {getActionLabel(action)}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          </>
        )}

        <Button
          size='small'
          variant='primary'
          onClick={handleExecuteAction}
          disabled={!selectedAction || isLoading || !hasActions}
        >
          {getActionIcon()}
        </Button>

        <TaskSettingsPopover
          task={task}
          workflowId={workflowId}
          withPortal={withPortal}
          enrollmentId={enrollmentId}
        />
      </div>

      <BlockTaskDialog
        task={task}
        isOpen={isBlockDialogOpen}
        onClose={() => setIsBlockDialogOpen(false)}
        // Don't pass onSuccess - the dialog handles its own query invalidation
        // Passing handleDialogSuccess would cause it to try executing the action again
      />

      <UnblockTaskDialog
        task={task}
        isOpen={isUnblockDialogOpen}
        onClose={() => setIsUnblockDialogOpen(false)}
        // Don't pass onSuccess - the dialog handles its own query invalidation
        // Passing handleDialogSuccess would cause it to try executing the action again
      />
    </>
  )
}

// Hook for task actions
type UseTaskActionsOptions = {
  onSuccess?: (taskId: string, action: string) => void
  onError?: (error: unknown) => void
}

export const useTaskActions = (
  workflowId: string,
  options?: UseTaskActionsOptions
) => {
  const queryClient = useQueryClient()
  const taskMutation = usePatchBrokersTasks()

  const handleTaskAction = (taskId: string, action: string) => {
    const statusMessage = 'Task manually updated by broker'

    taskMutation.mutate(
      {
        data: {
          task_ids: [taskId],
          status_event: action as any,
          status_message: statusMessage,
        },
      },
      {
        onSuccess: () => {
          // Refetch the workflow to get updated task statuses
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
          })

          options?.onSuccess?.(taskId, action)
        },
        onError: (error) => {
          console.error('Failed to execute task action:', error)
          alert('Failed to execute task action. Please try again.')

          options?.onError?.(error)
        },
      }
    )
  }

  return { handleTaskAction, isLoading: taskMutation.isPending }
}

export default TaskActionControls
